import type { DataType } from '@glueapp/graph-types';

/**
 * Resolves a generic's resolvedType iteratively at the top level only.
 * Avoids the recursive walk in `unwrapAllResolvedGenerics`, which throws on
 * malformed/partial type objects (e.g. an object type without `properties`,
 * a function type without `inputTypes`) that can appear on node-library
 * templates before they're rendered in a real graph.
 */
function unwrapTopLevelGeneric(type: DataType | undefined | null): DataType | undefined {
    let current: DataType | undefined | null = type;
    while (current && current.kind === 'generic' && current.resolvedType) {
        current = current.resolvedType;
    }
    return current ?? undefined;
}

function containsObjectAnywhere(type: DataType | undefined | null, depth: number): boolean {
    if (!type || depth > 16) return false;
    switch (type.kind) {
        case 'object':
            return true;
        case 'array':
            return containsObjectAnywhere(type.subType, depth + 1);
        case 'union':
            return Array.isArray(type.subTypes) && type.subTypes.some(subType => containsObjectAnywhere(subType, depth + 1));
        case 'optional':
            return containsObjectAnywhere(type.innerType, depth + 1);
        case 'generic':
            return type.resolvedType ? containsObjectAnywhere(type.resolvedType, depth + 1) : false;
        default:
            return false;
    }
}

/**
 * Returns true when a port's type should offer an "Unpack" quick-action:
 * either the type itself is an object, or it's an array/list whose nested
 * structure contains at least one object anywhere.
 */
export function canUnpackFromType(type: DataType | undefined | null): boolean {
    const unwrapped = unwrapTopLevelGeneric(type);
    if (!unwrapped) return false;
    if (unwrapped.kind === 'object') return true;
    if (unwrapped.kind === 'array') return containsObjectAnywhere(unwrapped.subType, 0);
    return false;
}

/**
 * Returns true when a port's type should offer a "Build Object" quick-action:
 * the type itself (after generic/optional unwrapping) is an object.
 */
export function canBuildObjectForType(type: DataType | undefined | null): boolean {
    let unwrapped = unwrapTopLevelGeneric(type);
    if (unwrapped && unwrapped.kind === 'optional') {
        unwrapped = unwrapTopLevelGeneric(unwrapped.innerType);
    }
    return unwrapped?.kind === 'object';
}

/**
 * Returns the underlying object DataType for a port that passes
 * `canBuildObjectForType`. Returns undefined otherwise.
 */
export function getBuildObjectTargetType(type: DataType | undefined | null): DataType | undefined {
    let unwrapped = unwrapTopLevelGeneric(type);
    if (unwrapped && unwrapped.kind === 'optional') {
        unwrapped = unwrapTopLevelGeneric(unwrapped.innerType);
    }
    return unwrapped?.kind === 'object' ? unwrapped : undefined;
}
